import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Button } from '../../ui/button';
import { BadgeX, MessageSquarePlus, TriangleAlert } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Cookies from 'js-cookie';
import { TOKEN_NAME } from '../../../constants';
import { doPerformOrderAction } from '../../../modules/orders/store/thunkts';
import { InfoTooltip } from '../../../components/Elements/InfoTooltip';
import { WarningProgress } from '../../ui/progress';
import { differenceInDays } from 'date-fns';
import { doCreateMissingComment } from '../../../modules/issues/stores/thunk';
import { useForm } from 'react-hook-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '../../ui/form';
import { Textarea } from '../../ui/textarea';

const CriticalDelayOrders = () => {
  const dispatch = useDispatch<AppDispatch>();
  const form = useForm();
  const [commentDialogFor, setCommentDialogFor] = useState<string>(null);
  const [message, setMessage] = useState<string>(null);

  const history = useHistory();
  const [cancelledOrders, setCancelledOrders] = useState([]);

  useEffect(() => {
    fetchCritcialDelayOrders();
  }, []);

  const fetchCritcialDelayOrders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/dashboard/critical-delay-orders`, {
        headers: {
          Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const res = await response.json();
      setCancelledOrders(res.orders);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveOrder = async (orderId: string, action: 'MARKED_AS_MISSING', e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(doPerformOrderAction({ orderId, action }));
    fetchCritcialDelayOrders();
  };

  const openMissingDialog = (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentDialogFor(orderId);
  };

  const createMissingComment = async () => {
    await dispatch(doCreateMissingComment({ orderId: commentDialogFor, message }));
    fetchCritcialDelayOrders();
  };

  const sortedCancelledOrders = useMemo(
    () =>
      [...cancelledOrders]
        ?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 10),
    [cancelledOrders],
  );

  const handleRowClick = (orderId: string, e: React.MouseEvent) => {
    const directTo = `/orders/${orderId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(directTo, '_blank');
    } else {
      history.push(directTo);
    }
  };

  const getAutoMissingCountdown = (order: any) => {
    const diff = differenceInDays(new Date(), new Date(order.deliveryDate));
    return Math.round((diff / 7) * 100);
  };
  const getDaysToGo = (order: any) => {
    return 7 - differenceInDays(new Date(), new Date(order.deliveryDate));
  };

  const getComment = (o: any) => {
    const m = o.issues.find((issue) => issue.reason === 'delayed' && issue.message === 'Critically delayed comment')
      ?.comments?.[0]?.message;
    return m ? `${m} (click to add more comments).` : undefined;
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>
          <div className="flex justify-between items-center">
            <span className="inline-flex items-center">
              <TriangleAlert className="mr-1 h-4 w-4" />
              Critically delayed orders
            </span>
            <div className="ml-auto">
              <InfoTooltip title="Orders are marked as critically delayed when both the delivery date and last status update where > 2 days ago, and the order has not had two attempts yet. You can mark these orders as missing which will notify the merchant, or you can schedule a new delivery to remove them from the view. If an order has been delayed for > 7 days, we will automatically mark the order as missing and file a claim" />
            </div>
          </div>
        </CardTitle>
        <CardDescription>
          These orders are critically delayed. To remove the orders from the view, schedule them for a new delivery
          attempt. If the order is missing, please let us know.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {/* <TableHead>Order ID</TableHead> */}
              <TableHead colSpan={2}>Barcodes</TableHead>
              <TableHead colSpan={2}>Recipient Name</TableHead>

              <TableHead>Remaining</TableHead>

              <TableHead colSpan={2} className="text-right">
                Missing?
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cancelledOrders?.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                  No critically delayed orders that need your attention, enjoy your day!
                </TableCell>
              </TableRow>
            ) : (
              <AnimatePresence mode="popLayout">
                {sortedCancelledOrders.map((order) => (
                  <motion.tr
                    key={order.orderId}
                    initial={{ opacity: 1, height: 'auto' }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        duration: 0.2,
                        ease: 'easeOut',
                      },
                    }}
                    onClick={(e) => handleRowClick(order.orderId, e)}
                    className="hover:cursor-pointer hover:bg-gray-50"
                    style={{ display: 'table-row' }}
                    layout
                  >
                    <TableCell
                      colSpan={2}
                      key={order.barcode}
                      className="max-w-56 overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      {order.barcode}
                    </TableCell>
                    <TableCell colSpan={2} className="max-w-56 overflow-hidden text-ellipsis whitespace-nowrap">
                      {order.deliveryName}
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="w-full">
                            <WarningProgress value={getAutoMissingCountdown(order)} className="mr-2 " />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{getDaysToGo(order)} days until claim is filed</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-center overflow-hidden text-ellipsis whitespace-nowrap">
                      <div className="flex justify-end items-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="relative">
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={(e) => openMissingDialog(order.orderId, e)}
                                >
                                  <MessageSquarePlus />
                                </Button>
                                {getComment(order) && (
                                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{getComment(order) || 'Leave extra info about this order'}</p>

                              <p></p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                    <TableCell className="text-center overflow-hidden text-ellipsis whitespace-nowrap">
                      <div className="flex justify-end items-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={(e) => handleRemoveOrder(order.orderId, 'MARKED_AS_MISSING', e)}
                              >
                                <BadgeX />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Mark order as missing</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </TableBody>
          {cancelledOrders?.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={6}>Total Critically Delayed orders</TableCell>

                <TableCell className="text-right">{cancelledOrders?.length}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
      <AlertDialog open={!!commentDialogFor} onOpenChange={(v) => !v && setCommentDialogFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Extra info on critically delayed order</AlertDialogTitle>
            <AlertDialogDescription>Please share your findings on this critically delayed order</AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Form {...form}>
              <FormField
                control={form.control}
                name="_"
                render={() => (
                  <FormItem className="mt-4">
                    <FormControl>
                      <Textarea
                        rows={5}
                        placeholder="Extra info"
                        className="resize-none"
                        onBlur={(v) => setMessage(v?.target?.value)}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Form>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(v) => createMissingComment()}>Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CriticalDelayOrders;
